import React, { createContext, useReducer } from 'react';
import { 
  generateCoherentColorSet, 
  darkenHexColor as darkenColor, 
  lightenHexColor as lightenColor
} from '../utils/common/colorUtils';

// Generate random color
const randomColor = () => {
  const getHex = () => Math.floor(Math.random() * 256).toString(16).padStart(2, '0');
  return `#${getHex()}${getHex()}${getHex()}`;
};

// Generate initial color palette
const generateColorPalette = () => {
  // Generate base colors
  const facadeColor = randomColor();
  const groundFloorColor = randomColor();
  const WindowColorA = randomColor();
  const WindowColorB = randomColor();
  const windowFrameColor = '#333333';
  const sideWallColor = randomColor();
  const vitrineColor = '#AAAAFF';
  const vitrineAwningColor = randomColor();
  const vitrineFrameColor = lightenColor(vitrineAwningColor, 0.3); // Lighter version of awning color
  const balconyColor = randomColor();
  const awningColor = randomColor();
  const roofColor = randomColor();
  const roofAccessColor = randomColor();

  return {
    facadeColor,
    groundFloorColor,
    WindowColorA,
    WindowColorB,
    windowFrameColor,
    sideWallColor,
    vitrineColor,
    vitrineAwningColor,
    vitrineFrameColor,
    balconyColor,
    awningColor,
    roofColor,
    roofAccessColor
  };
};

// Generate initial coherent colors
const baseColors = generateCoherentColorSet();
const initialColorState = {
  ...baseColors,
  // Keep original window colors
  WindowColorA: '#000080',
  WindowColorB: '#b8ebff',
  // Make window frames a darker shade of facade color
  windowFrameColor: darkenColor(baseColors.facadeColor, 0.4),
  // Make vitrine frames a darker shade of ground floor color
  vitrineFrameColor: darkenColor(baseColors.groundFloorColor, 0.3),
  // Make balcony a darker shade of facade color
  balconyColor: darkenColor(baseColors.facadeColor, 0.5),
  // Make window awning a darker shade of facade color
  awningColor: darkenColor(baseColors.facadeColor, 0.6),
  // Level lines color - dark shade of facade
  levelLinesColor: darkenColor(baseColors.facadeColor, 0.2),
  // Guardrail color - lighter shade of roof color
  guardrailColor: lightenColor(baseColors.roofColor, 0.2),
};

// Generate initial state
const initialState = {
  levelHeight: 16,
  groundFloorHeight: 18,
  depth: 67,
  color: '#dedede',
  showGround: true,
  levelNum: 3,
  windowConfig: 'config4',
  showLevelFloors: false,
  showLevelLines: true,
  windowTop: 8,
  windowBottom: 0,
  windowInset: 0, // Amount to inset windows into the facade (0-3)
  doorHeight: 12,
  doorWidth: 8,
  doorPosition: 'center',
  showEdges: false,
  balconyDepth: 1,           // new: number of balcony voxels in the Z direction (extrusion)
  balconyHeight: 1, 
  balconySideOffset: 0,
  showBalconies: true,
  // Vitrine settings
  showVitrine: true,
  vitrineOffset: 2,
  vitrineColor: '#AAAAFF',
  showVitrineFrames: true,
  vitrineFrameDivision: 2,
  showVitrineAwning: true,
  vitrineAwningHeight: 2,
  vitrineAwningDepth: 3,
  vitrineAwningColor: '#8D6E4A',
  // New awnings parameters (mirrored vertically)
  awningDepth: 1,
  awningHeight: 1,
  awningSideOffset: 0,
  showAwnings: false,
  // Side wall windows
  showLeftSideWindows: false,
  showRightSideWindows: false,
  showRoof: true,
  showWindowFrames: true,
  roofAwning: 1,
  roofThickness: 2,
  showRoofAccess: true, // Toggle for roof access cube visibility
  roofAccessWidth: 10,
  roofAccessHeight: 10,
  roofAccessDepth: 10,
  roofAccessOffset: 10,
  roofAccessDoorWidth: 6, // Width of the door on the roof access cube
  roofAccessDoorHeight: 8, // Height of the door on the roof access cube
  showBrickPattern: false, // Toggle for brick pattern on side walls
  brickContrast: 0.1, // Contrast level for brick pattern (-1 to 1)
  randomEraseBricks: 0, // Amount of section-based brick erasure (0-1)
  randomEraseFine: 0, // Amount of individual brick erasure (0-1)
  brickOnSideWalls: true, // Apply brick pattern to side walls
  brickOnFacade: false, // Apply brick pattern to facade
  brickOnGroundFloor: false, // Apply brick pattern to ground floor
  // Apply all the color values from initialColorState
  ...initialColorState
};

// Reducer function for the BuildingContext
const buildingReducer = (state, action) => {
  const { field, value, palette } = action;

  if (field === 'regenerateColors') {
    // Generate new colors - either from a provided palette or generate coherent set
    let newColors;
    
    if (palette && Array.isArray(palette) && palette.length > 0) {
      // Use the provided palette from Nounish Colors feature
      console.log('Using custom palette:', palette);
      
      // Ensure we have enough colors, if not, repeat or generate random ones
      const ensurePaletteColor = (index) => {
        if (palette.length === 0) return randomColor();
        return palette[index % palette.length];
      };
      
      newColors = {
        facadeColor: ensurePaletteColor(0),
        sideWallColor: ensurePaletteColor(1),
        groundFloorColor: ensurePaletteColor(2),
        roofColor: ensurePaletteColor(3),
        balconyColor: ensurePaletteColor(4),
        awningColor: ensurePaletteColor(5),
        vitrineColor: ensurePaletteColor(6),
        vitrineAwningColor: ensurePaletteColor(7),
        roofAccessColor: ensurePaletteColor(8),
      };
      
      // Derive related colors from the palette colors
      newColors.windowFrameColor = darkenColor(newColors.facadeColor, 0.4);
      newColors.vitrineFrameColor = darkenColor(newColors.groundFloorColor, 0.3);
      newColors.levelLinesColor = darkenColor(newColors.facadeColor, 0.4);
      newColors.guardrailColor = lightenColor(newColors.roofColor, 0.2);
      
      // Keep original window colors unless we have more palette colors
      if (palette.length > 9) {
        newColors.WindowColorA = ensurePaletteColor(9);
      } else {
        newColors.WindowColorA = state.WindowColorA;
      }
      
      if (palette.length > 10) {
        newColors.WindowColorB = ensurePaletteColor(10);
      } else {
        newColors.WindowColorB = state.WindowColorB;
      }
    } else {
      // Generate coherent set with existing logic
      newColors = generateCoherentColorSet();
      // Keep original window colors
      newColors.WindowColorA = state.WindowColorA;
      newColors.WindowColorB = state.WindowColorB;
      // Make window frames a darker shade of facade color
      newColors.windowFrameColor = darkenColor(newColors.facadeColor, 0.4);
      // Make vitrine frames a darker shade of ground floor color
      newColors.vitrineFrameColor = darkenColor(newColors.groundFloorColor, 0.3);
      // Make balcony a darker shade of facade color
      newColors.balconyColor = darkenColor(newColors.facadeColor, 0.5);
      // Make window awning a darker shade of facade color
      newColors.awningColor = darkenColor(newColors.facadeColor, 0.6);
      // Level lines color - dark shade of facade
      newColors.levelLinesColor = darkenColor(newColors.facadeColor, 0.4);
      // Guardrail color - lighter shade of roof color
      newColors.guardrailColor = lightenColor(newColors.roofColor, 0.2);
    }
    
    return {
      ...state,
      ...newColors
    };
  }

  // Handle other state updates
  return {
    ...state,
    [field]: value
  };
};

export const BuildingContext = createContext();

export const BuildingProvider = ({ children }) => {
  const [state, dispatch] = useReducer(buildingReducer, initialState);
  return (
    <BuildingContext.Provider value={{ state, dispatch }}>
      {children}
    </BuildingContext.Provider>
  );
};